// Example stat definition
/*        example_stat_definition = {
            "name": "boat_stats",
            "display_name": "Boat Stats",
            "description": "Stores data received from boats such as engine stats and system information.",
            "data_frequency": 0,
            "parent_type": "asset",
            "field_ordering": null,
            "field_schemas": {
                "engine_rpm": {
                    # ** REQUIRED FIELDS ** #

                    # [OVERRIDABLE IN VIEW] The name displayed in the front-end
                    "display_name": "Engine RPM",

                    # The field's type. The available options are "number", or "string"
                    "type": "number",

                    # ** OPTIONAL FIELDS ** #

                    # [OVERRIDABLE IN VIEW] The color_config is used to define the colouring on the front end for this particular field.
                    # This affects the map, table and status colours.
                    "color_config": {
                        # points takes an array of {value, color} objects where the colour is [r,g,b,a] and the value is a number or string
                        "points": [{"value": 0, "color": [255, 0, 0, 1]}, {"value": 100, "color": [0, 255, 0, 1]}],
                        # default defines the default [r,g,b,a] colour
                        "default": [255, 255, 255, 1],
                        # type defines either gradient, traffic_light or exact (this one being mostly used for string field types)
                        "type": "gradient"
                    },

                    # [OVERRIDABLE IN VIEW] The units (used for display purposes)
                    "units": {
                        "suffix": "rpm"
                    },

                    # [OVERRIDABLE IN VIEW] A description of the field
                    "description": "The RPM of the boat's engine.",

                    # [OVERRIDABLE IN VIEW] Whether or not this field should be hidden from api requests and the front-end.
                    # This is used if you wish to remove a field but keep the data
                    "hidden": False,

                    # The original logical name of this field. If you wish to rename it, the initial logical name of this field (with no clashes with other fields)
                    # must be set here. This is used as a reference so that the renamed field is properly tracked instead of being deleted and re-added
                    # DEFAULT: this field's name (key)
                    "original_name": "engine_revolutions"
                }
            }
            "is_relationship": false
        }
*/

import { Operator } from '../operators';

export type Color = [number, number, number, number] | [number, number, number];

export interface ColorConfigPoint<T = string | number> {
    value: T;
    color: Color;

    /**
     * Whether the show the point in a UI representation of the color config (ie, color legend)
     * Probably only used in a "gradient" number line
     */
    display?: boolean;
}

const NUMBER_FORMAT_DECIMAL_PLACES = 2;

export type ColorConfigType = 'gradient' | 'traffic_light' | 'exact';

export interface ColorConfig<PointValueType = string | number, Type = ColorConfigType> {
    // This takes an array of {value, color} objects where the colour is [r,g,b,a] and
    // the value is a number or string
    points: ColorConfigPoint<PointValueType>[];
    // Type defines either gradient, traffic_light or exact (this one being mostly used for string field types)
    type: Type;
    // Default defines the default [r,g,b,a] colour that will be used for null stats
    default?: Color;
    // Whether or not to reverse the direction of colouring
    reverse?: boolean;
    // Display the desired name instead of "Default" in color legend
    default_name?: string;
}

// sorry for another one of these types - hopefully typescript will complain somewhere if it's not keep upto date!
export type ForeignKeyFieldType =
    | 'asset'
    | 'ip_device'
    | 'radio'
    | 'zone'
    | 'ticket'
    | 'trigger'
    | 'user'
    | 'censored_system';

export const FIELD_SCHEMAS__TYPE_NUMBER = 'number';
export const FIELD_SCHEMAS__TYPE_TENLOGTEN = 'tenlogten';
export const FIELD_SCHEMAS__TYPE_LON_LAT = 'lon_lat'; // same as point except this only exists in the front-end as a "hack"
export const FIELD_SCHEMAS__TYPE_POINT = 'point'; // same as lat_log except from the API directly
export const FIELD_SCHEMAS__TYPE_POLYGON = 'polygon';
export const FIELD_SCHEMAS__TYPE_LINE_STRING = 'linestring';
export const FIELD_SCHEMAS__TYPE_STRING = 'string';
export const FIELD_SCHEMAS__TYPE_BOOLEAN = 'boolean';
export const FIELD_SCHEMAS__TYPE_IMAGE = 'image' as const;
export const FIELD_SCHEMAS__TYPE_ASSET = 'asset';
export const FIELD_SCHEMAS__TYPE_IP_DEVICE = 'ip_device';
export const FIELD_SCHEMAS__TYPE_RADIO = 'radio';
export const FIELD_SCHEMAS__TYPE_CENSORED_SYSTEM = 'censored_system';
export const FIELD_SCHEMAS__TYPE_SYSTEM = 'system';
export const FIELD_SCHEMAS__TYPE_ZONE = 'zone';
const FIELD_SCHEMAS__TYPE_TICKET = 'ticket';
export const FIELD_SCHEMAS__TYPE_TIMESTAMP = 'timestamp';
const FIELD_SCHEMAS__TYPE_TRIGGER = 'trigger';
const FIELD_SCHEMAS__TYPE_USER = 'user';
export const FIELD_SCHEMAS__TYPE_FILE = 'file';

const FOREIGN_KEY_FIELD_SCHEMA_TYPES: Array<ForeignKeyFieldType> = [
    FIELD_SCHEMAS__TYPE_ZONE,
    FIELD_SCHEMAS__TYPE_ASSET,
    FIELD_SCHEMAS__TYPE_IP_DEVICE,
    FIELD_SCHEMAS__TYPE_TICKET,
    FIELD_SCHEMAS__TYPE_TRIGGER,
    FIELD_SCHEMAS__TYPE_USER,
    FIELD_SCHEMAS__TYPE_RADIO,
    FIELD_SCHEMAS__TYPE_CENSORED_SYSTEM,
];

const PRIMITIVE_FIELD_SCHEMA_TYPES = [
    FIELD_SCHEMAS__TYPE_NUMBER,
    FIELD_SCHEMAS__TYPE_TENLOGTEN,
    FIELD_SCHEMAS__TYPE_STRING,
    FIELD_SCHEMAS__TYPE_BOOLEAN,
    FIELD_SCHEMAS__TYPE_LON_LAT,
    FIELD_SCHEMAS__TYPE_POINT,
    FIELD_SCHEMAS__TYPE_POLYGON,
    FIELD_SCHEMAS__TYPE_IMAGE,
    FIELD_SCHEMAS__TYPE_FILE,
    FIELD_SCHEMAS__TYPE_TIMESTAMP,
];

const ALL_FIELD_SCHEMA_TYPES = (FOREIGN_KEY_FIELD_SCHEMA_TYPES as Array<string>).concat(
    PRIMITIVE_FIELD_SCHEMA_TYPES,
);

export interface FieldSchemaUnit {
    suffix: string;
    multiplier?: number;
    max_value?: number;
}

export interface FieldSchemaUnits {
    suffix?: string;
    multiplier?: number;
    values?: FieldSchemaUnit[];
}

export type AnyFieldSchemaType =
    | 'number'
    | 'tenlogten'
    | 'string'
    | 'boolean'
    | 'image'
    | 'asset'
    | 'ip_device'
    | 'zone'
    | 'ticket'
    | 'trigger'
    | 'user'
    | 'radio'
    | 'lon_lat'
    | 'point'
    | 'polygon'
    | 'linestring'
    | 'censored_system'
    | 'system'
    | 'file'
    | 'timestamp';

export interface FieldSchema {
    // [OVERRIDABLE IN VIEW] The name displayed in the front-end
    display_name?: string;
    // The field's type. The available options match the type constants specified above
    type?: AnyFieldSchemaType;
    // [OVERRIDABLE IN VIEW] The format for the type "number" field in the front-end. Available options are "int", "float", and "symbol"
    // with "float" being the default
    number_display_format?: string;
    // [OVERRIDABLE IN VIEW] The number of decimal places we want when the type is a number and the number_display_format is a "float"
    // with the value 2 being the default
    decimal_places?: number;
    // [OVERRIDABLE IN VIEW] The color_config is used to define the colouring on the front end for this
    // particular field. This affects the map, table and status colours.
    color_config?: ColorConfig;
    // [OVERRIDABLE IN VIEW] The units (used for display purposes)
    units?: FieldSchemaUnits;
    // [OVERRIDABLE IN VIEW] A description of the field
    description?: string;
    // [OVERRIDABLE IN VIEW] Whether or not this field should be hidden from the front-end.
    hidden?: boolean;
    // NOTE: THIS FIELD IS NOT CURRENTLY USED AND IS JUST HERE FOR FUTURE-PROOFING REASONS
    // The original logical name of this field. If you wish to rename it, the initial logical name of this field
    // (with no clashes with other fields) must be set here. This is used as a reference so that the renamed field
    // is properly tracked instead of being deleted and re-added
    // DEFAULT: this field's name (key)
    original_name?: string;
    // Optional extra field to allow defining a widget to be used in a table cell
    table_cell_widget?: 'macAndIpAutoLinker';
    // whether this field is being used a primitive grouping on the table
    is_subgroup?: boolean;
    // List of operators used in heatmap filtering
    operators?: Operator[];
}

export const STAT_DEFINITION__PARENT_TYPE_ASSET = 'asset';
export const STAT_DEFINITION__PARENT_TYPE_IP_DEVICE = 'ip_device';
export const STAT_DEFINITION__PARENT_TYPE_RADIO = 'radio';
export const STAT_DEFINITION__PARENT_TYPE_ZONE = 'zone';
export const STAT_DEFINITION__PARENT_TYPE_SYSTEM = 'system';

interface RollupDefinition {
    max_data_age_days?: number;
    time_slice_mins: number;
}

export interface StatDefinition {
    // The logical name used in the database and api endpoints
    name?: string;
    // [OVERRIDABLE IN VIEW] The name displayed in the front-end
    display_name?: string;
    // [OVERRIDABLE IN VIEW] A description of the table
    description?: string;
    // [OVERRIDABLE IN VIEW] A description of the table
    field_ordering?: [string];
    // The frequency of data in the table in seconds. Use 0 if frequency is unknown and the
    // table is used to store state
    data_frequency?: number; // how frequent the stats data is per asset/ip device
    // The parent table of these stats (whether or not they act as ip device, asset or zone dynamic stats)
    // The choices are 'asset', 'ip_device', 'zone' or 'radio' (the constants specified above)
    parent_type?: 'asset' | 'ip_device' | 'zone' | 'radio';
    // The schemas of the table columns/fields.
    // The keys will be used as the logical name of the columns (used in api and database)
    field_schemas?: {
        [key: string]: FieldSchema;
    };
    // is_relationship indicate if the underlying dynamic stats is a relationship
    is_relationship?: boolean;
    hide_from_heat_map?: boolean;

    // HACK sometimes you really just need to know if the URL endpoint should have the /dynamic_stats/ prefix in it... I'm sorry
    //  given a stat definition, we now have the ability to create the API URL for it, eg: /api/dynamic_stats/stat_name/
    isDynamicStat?: true;

    rollups?: RollupDefinition[];

    default_views?: Array<string>;
    max_data_age_days?: number;
    updatable?: boolean;
    show_internal_id?: boolean;
}

export function isValidFieldSchemaType(type: string): boolean {
    return ALL_FIELD_SCHEMA_TYPES.includes(type);
}

export function isForeignKeyField(type: string | undefined): type is ForeignKeyFieldType {
    if (!type) {
        return false;
    }
    return FOREIGN_KEY_FIELD_SCHEMA_TYPES.includes(type as ForeignKeyFieldType);
}

/**
 * When provided with a statDefinition, this function returns whether or not any field is a subgroup field
 * @param statDefinition
 */
export function statDefinitionHasSubgroupField(statDefinition: StatDefinition): boolean {
    const fieldSchemas = statDefinition.field_schemas;
    for (const fieldSchemaKey in fieldSchemas) {
        if (fieldSchemas[fieldSchemaKey].is_subgroup) {
            return true;
        }
    }
    return false;
}

export function getPropertyNameForStatParentField(statDefinition: StatDefinition): string {
    return {
        [FIELD_SCHEMAS__TYPE_ASSET]: 'asset_id',
        [FIELD_SCHEMAS__TYPE_IP_DEVICE]: 'ip_device_id',
        [FIELD_SCHEMAS__TYPE_RADIO]: 'radio_id',
        [FIELD_SCHEMAS__TYPE_ZONE]: 'zone_id',
        [FIELD_SCHEMAS__TYPE_TICKET]: 'ticket_id',
        [FIELD_SCHEMAS__TYPE_TRIGGER]: 'trigger_id',
        [FIELD_SCHEMAS__TYPE_USER]: 'user_id',
    }[statDefinition.parent_type];
}

export function getPropertyNameForSubgroupField(statDefinition: StatDefinition): string | null {
    return Object.keys(statDefinition.field_schemas).find(
        (key) => statDefinition.field_schemas[key].is_subgroup,
    );
}

export function getDecimalPlaces(fieldSchema: FieldSchema): number {
    if (fieldSchema.type === FIELD_SCHEMAS__TYPE_NUMBER) {
        const isInt =
            fieldSchema.number_display_format == 'int' ||
            fieldSchema.number_display_format == 'symbol';
        if (isInt) {
            return 0;
        }

        if (fieldSchema.number_display_format == 'float') {
            return fieldSchema.decimal_places || NUMBER_FORMAT_DECIMAL_PLACES;
        }
    }

    return NUMBER_FORMAT_DECIMAL_PLACES;
}
